// src/admin/ClassAnalysis.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import { getFirestore, collection, getDocs, query, orderBy, limit as firestoreLimit } from "firebase/firestore";
import DiscPersonalReport from "../disc/report/DiscPersonalReport";
import RaporYazdirma from "./RaporYazdirma";
import { formatDuration, getDurationMs } from "../utils/formatDuration";
import { getAdminText } from "./adminSozluk";
import { DISC_RENK, ACI_ARALIKLARI, HARF_ICERIK } from "../disc/discBrand";
import { sayimlariCikar } from "../disc/discItems";
import { uyarlanmisSkorlar, dogalSkorlar } from "../disc/discScoring";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, Title);

/* ========================
   SABİTLER
======================== */
const LABELS = ["D", "I", "S", "C"];

// Renkler ve çeyrek düzeni artık paylaşım belgesinden geliyor (src/disc/discBrand.js).
// ÖNCE: burada ayrı bir palet (#e53935/#fdd835/#43a047/#1e88e5) ve belgeyle uyuşmayan
// bir çeyrek sırası (D sağ-üst, C sol-üst) vardı; rapor ile grup grafiği farklı
// yerlere farklı harfler koyuyordu.
const DISC_COLORS = DISC_RENK;
const ANGLE_RANGES = ACI_ARALIKLARI;

// Etiketler artık dile göre üretilir; anahtarlar (before_1965, executive, ...)
// Firestore'da saklanan DEĞERLERdir ve ASLA çevrilmez — yalnız görünen metin değişir.
const GENERATION_LABELS = (lang) => ({
  before_1965: getAdminText(lang).oncesi1964,
  gen_x_1965_1980: "1965–1980",
  gen_y_1981_1996: "1981–1996",
  gen_z_1997_2012: "1997–2012",
});

const ROLE_LABELS = (lang) => {
  const A = getAdminText(lang);
  return {
    executive: A.rolUst,
    director: A.rolDirektor,
    manager: A.rolMudur,
    team_leader: A.rolTakimLideri,
    specialist: A.rolUzman,
    operations: A.rolOperasyon,
    student: A.rolOgrenci,
    other: A.rolDiger,
  };
};

/* ========================
   YARDIMCILAR
======================== */
function interp(mapObj, x) {
  const keys = Object.keys(mapObj).map(Number).sort((a, b) => a - b);
  if (x <= keys[0]) return mapObj[keys[0]];
  if (x >= keys[keys.length - 1]) return mapObj[keys[keys.length - 1]];

  for (let i = 0; i < keys.length - 1; i++) {
    const x0 = keys[i];
    const x1 = keys[i + 1];

    if (x >= x0 && x <= x1) {
      const y0 = mapObj[x0];
      const y1 = mapObj[x1];
      const t = (x - x0) / (x1 - x0);
      return Math.round(y0 + t * (y1 - y0));
    }
  }

  return 0;
}

function percentageToRadius(p) {
  if (p < 50) return null;
  const minR = 0.30;
  const maxR = 1.00;
  return minR + ((p - 50) / 50) * (maxR - minR);
}

function distributeAngles(startDeg, endDeg, count) {
  const step = (endDeg - startDeg) / (count + 1);
  return Array.from({ length: count }, (_, i) => startDeg + step * (i + 1));
}

function polarToXY(radius, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
}

function makeEmptyBuckets() {
  return { D: {}, I: {}, S: {}, C: {} };
}

// Kovaya artık sadece "bir tane daha var" değil, KİMİN olduğu da yazılır.
// Önceden `push(1)` yapılıyordu ve kimlik grafiğe giderken kayboluyordu; tooltip de
// bu yüzden anlamsız "(-0,286, 0,286)" koordinatı gösteriyordu.
function addToBucket(buckets, letter, percent, ad) {
  const key = String(percent);
  if (!buckets[letter][key]) buckets[letter][key] = [];
  buckets[letter][key].push(ad || "");
}

function buildScatterDatasetFromBuckets(buckets, discDagilimiEtiketi) {
  const points = [];

  for (const letter of LABELS) {
    const [start, end] = ANGLE_RANGES[letter];
    const perMap = buckets[letter];

    for (const percentStr of Object.keys(perMap)) {
      const percent = Number(percentStr);
      const count = perMap[percentStr].length;

      const r = percentageToRadius(percent);
      if (r == null) continue;

      const angles = distributeAngles(start, end, count);
      angles.forEach((deg, i) => {
        const { x, y } = polarToXY(r, deg);
        const size = 3 + (percent - 50) * 0.15;
        points.push({
          x,
          y,
          r: size,
          backgroundColor: DISC_COLORS[letter],
          // Tooltip için: açı sırası ile kova sırası aynıdır, i ile eşleşir.
          ad: perMap[percentStr][i] || "",
          harf: letter,
          yuzde: percent,
        });
      });
    }
  }

  return {
    datasets: [
      {
        label: discDagilimiEtiketi,
        data: points,
        pointRadius: points.map((p) => p.r),
        pointBackgroundColor: points.map((p) => p.backgroundColor),
        pointHoverRadius: points.map((p) => p.r + 2),
        showLine: false,
      },
    ],
  };
}

function buildChartDataFromRecords(sourceRecords, discDagilimiEtiketi) {
  const adaptedBuckets = makeEmptyBuckets();
  const naturalBuckets = makeEmptyBuckets();

  sourceRecords.forEach((row) => {
    const adaptedPerc = getAdaptedScores(row);
    const naturalPerc = getNaturalScores(row);

    const ad = getParticipantName(row);
    for (const L of LABELS) {
      if (adaptedPerc[L] >= 50) addToBucket(adaptedBuckets, L, adaptedPerc[L], ad);
      if (naturalPerc[L] >= 50) addToBucket(naturalBuckets, L, naturalPerc[L], ad);
    }
  });

  return {
    adaptedData: buildScatterDatasetFromBuckets(adaptedBuckets, discDagilimiEtiketi),
    naturalData: buildScatterDatasetFromBuckets(naturalBuckets, discDagilimiEtiketi),
  };
}

// ÖNEMLİ — 18.08.2026: kayıtlı `reportSummary.adaptedScores/naturalScores`
// ARTIK KULLANILMIYOR. O yüzdeler kayıt anında ESKİ (şablondan farklı) tablolarla
// hesaplanmıştı ve kayıtlar değişmez (`update: if false`). Ham sayımlar ise
// doğrudur — 7 kaydın hepsinde şablon formülleriyle birebir doğrulandı.
// Bu yüzden yüzdeler her görüntülemede HAM SAYIMDAN yeniden üretilir; böylece
// eski kayıtlar da şablona göre doğru grafiği ve raporu verir.
function getAdaptedScores(row) {
  return uyarlanmisSkorlar(row?.mostCounts || {});
}

function getNaturalScores(row) {
  return dogalSkorlar(row?.leastCounts || {});
}

function getPrimaryStyle(row) {
  // Kayıtlı primaryStyle de eski tabloyla üretilmişti; ham sayımdan yeniden hesapla.
  const adapted = getAdaptedScores(row);
  return LABELS.map((key) => ({ key, value: adapted[key] || 0 })).sort((a, b) => b.value - a.value)[0]?.key || "-";
}

// Bir kaydı DiscPersonalReport'un beklediği prop setine çevirir.
// Hem sayfa içi görünüm hem baskı katmanı aynı kaynağı kullansın diye ayrıldı;
// iki yerde ayrı ayrı kurulursa biri güncellenip diğeri unutuluyor.
// RAPOR DİLİ — VARSAYILAN: ARAYÜZ DİLİ ("arayuz").
//
// 2026-08-21 DÜZELTME: varsayılan önce "kayit" (kaydın kendi dili) idi. Ajlan
// arayüzü İngilizce yapıp "Raporu Aç"a bastığında rapor Türkçe geliyordu — çünkü
// kayıtlar Türkçe doldurulmuştu. Teknik olarak doğruydu ama BEKLENEN davranış
// değildi: arayüz İngilizceyse rapor da İngilizce gelmelidir.
//
// Seçenekler: "arayuz" (varsayılan) · "kayit" · "tr" · "en"
function raporDiliSec(record, secim, arayuzDili) {
  if (secim === "tr" || secim === "en") return secim;
  if (secim === "kayit") return record?.lang === "en" ? "en" : "tr";
  return arayuzDili === "en" ? "en" : "tr";      // "arayuz"
}

function raporOzellikleri(record, raporDili, arayuzDili) {
  return {
    adaptedScores: getAdaptedScores(record),
    naturalScores: getNaturalScores(record),
    user: {
      name: record?.participant?.firstNameOrAlias || record?.name || record?.user?.name || "",
      surname: record?.participant?.lastName || record?.surname || "",
      generationRange: record?.participant?.generationRange || "",
      roleLevel: record?.participant?.roleLevel || "",
    },
    timestamp: formatDate(record),
    durationMs: getDurationMs(record),
    language: raporDiliSec(record, raporDili, arayuzDili),
    accessContext: record?.somerenAccess || {},
  };
}

// Eşik %50 VE ÜZERİ (>=), tek istisnasız. Burası projedeki tek `> 50` yeriydi;
// dağılım grafiği, rapor motoru (activeAdapted) ve sinyal panosu hep `>= 50`
// kullanıyordu. Tam 50 alan biri raporda görünüp bu sütunda görünmezdi.
//
// NOT: şablon tablolarıyla tam 50 üretilemiyor (0..24 arası tüm ham skorlar
// tarandı, hiçbiri 50 vermiyor) — yani bu bugün gözle görülür bir fark yaratmaz.
// Yine de kural her yerde aynı olsun diye düzeltildi; tablo bir gün değişirse
// sessizce tutarsız davranmasın.
function getStylesOver50(scores) {
  return LABELS
    .map((key) => ({ key, value: Number(scores?.[key] || 0) }))
    .filter((item) => item.value >= 50)
    .sort((a, b) => b.value - a.value);
}

function DiscProfileLetters({ scores }) {
  const items = getStylesOver50(scores);

  if (!items.length) {
    return <span style={{ color: "#6b7280", fontWeight: 800 }}>-</span>;
  }

  const sizes = [24, 19, 15, 12];
  const weights = [950, 850, 760, 680];

  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap" }}>
      {items.map((item, index) => (
        <span
          key={item.key}
          title={`${item.key}: ${Math.round(item.value)}%`}
          style={{
            color: DISC_COLORS[item.key] || "#111827",
            fontSize: sizes[index] || 12,
            fontWeight: weights[index] || 680,
            lineHeight: 1,
          }}
        >
          {item.key}
        </span>
      ))}
    </span>
  );
}

function getParticipantName(row) {
  const participant = row?.participant || {};
  const name =
    [participant.firstNameOrAlias, participant.lastName].filter(Boolean).join(" ").trim() ||
    [row?.name, row?.surname].filter(Boolean).join(" ").trim() ||
    row?.user?.name ||
    row?.user?.fullName ||
    "";
  return name;
}

function getGenerationLabel(value, lang) {
  return GENERATION_LABELS(lang)[value] || getAdminText(lang).belirtilmedi;
}

function getRoleLabel(value, lang) {
  return ROLE_LABELS(lang)[value] || getAdminText(lang).belirtilmedi;
}

function getTimestampMillis(row) {
  const value = row?.timestamp || row?.createdAt;
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatDate(row) {
  const ms = getTimestampMillis(row);
  if (!ms) return "-";
  return new Date(ms).toLocaleString("tr-TR");
}

function countBy(records, getter, lang) {
  return records.reduce((acc, record) => {
    const key = getter(record) || getAdminText(lang).belirtilmedi;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

/* ========================
   QUADRANT ARKA PLAN
======================== */
const quadrantPlugin = {
  id: "quadrantBackground",
  beforeDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const { left, top, width, height } = chartArea;
    const midX = left + width / 2;
    const midY = top + height / 2;

    ctx.save();
    ctx.globalAlpha = 0.10;

    // Belge düzeni: sol-üst D · sağ-üst I · sol-alt C · sağ-alt S
    ctx.fillStyle = DISC_COLORS.D;
    ctx.fillRect(left, top, width / 2, height / 2);

    ctx.fillStyle = DISC_COLORS.I;
    ctx.fillRect(midX, top, width / 2, height / 2);

    ctx.fillStyle = DISC_COLORS.C;
    ctx.fillRect(left, midY, width / 2, height / 2);

    ctx.fillStyle = DISC_COLORS.S;
    ctx.fillRect(midX, midY, width / 2, height / 2);

    ctx.restore();
  },
};

/* ========================
   ANA BİLEŞEN
======================== */
export default function ClassAnalysis({ lang = "tr" }) {
  const A = getAdminText(lang);
  const [raporDili, setRaporDili] = useState("arayuz");
  const db = getFirestore();

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  // 2026-08-16: baskıya hazır, Someren Labs markalı rapor katmanı (bkz. RaporYazdirma.jsx)
  const [yazdirilacakKayit, setYazdirilacakKayit] = useState(null);
  // Hangi PDF isteniyor: "tam" (Someren Labs raporu) veya "katilimci"
  // (katılımcının envanter sonunda gördüğü iki grafik).
  const [yazdirmaModu, setYazdirmaModu] = useState("tam");
  // Dağılım grafiklerinde isim gösterimi. VARSAYILAN KAPALI: grafik projeksiyona
  // vurulduğunda salonun herkesin skorunu adıyla görmemesi için. Açıkken isim
  // yalnız NOKTANIN ÜZERİNE GELİNCE çıkar, ekranda kalıcı etiket olmaz —
  // bir kişi ortalama 2,3 nokta bıraktığı için kalıcı etiket hem çakışır hem
  // aynı ismi 2-3 kez yazardı.
  const [isimGoster, setIsimGoster] = useState(false);
  const [scopeLimit, setScopeLimit] = useState("30");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [generationFilter, setGenerationFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  // Tablo sayfa sayfa gösterilir. Kapsam filtresi kaç kaydın OKUNACAĞINI,
  // sayfalama ise okunanların kaçının aynı anda ÇİZİLECEĞİNİ belirler.
  const [sayfa, setSayfa] = useState(1);
  const [error, setError] = useState("");

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { min: -1.05, max: 1.05, ticks: { display: false } },
        y: { min: -1.05, max: 1.05, ticks: { display: false } },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            // Varsayılan Chart.js etiketi x/y koordinatı yazıyordu ("(-0,286, 0,286)"),
            // kimseye bir şey ifade etmiyordu. Artık harf + yüzde; isim yalnız
            // anahtar açıkken eklenir.
            label: (ctx) => {
              const n = ctx.raw || {};
              const temel = `${n.harf} · %${n.yuzde}`;
              return isimGoster && n.ad ? `${n.ad} — ${temel}` : temel;
            },
          },
        },
      },
    }),
    [isimGoster]
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const resultsRef = collection(db, "disc_sonuclari");
        const snap =
          scopeLimit === "all"
            ? await getDocs(resultsRef)
            : await getDocs(
                query(
                  resultsRef,
                  orderBy("timestamp", "desc"),
                  firestoreLimit(Number(scopeLimit))
                )
              );
        const adaptedBuckets = makeEmptyBuckets();
        const naturalBuckets = makeEmptyBuckets();
        const loadedRecords = [];

        snap.forEach((docSnap) => {
          const row = { id: docSnap.id, ...docSnap.data() };
          loadedRecords.push(row);

          const adaptedPerc = getAdaptedScores(row);
          const naturalPerc = getNaturalScores(row);

          for (const L of LABELS) {
            if (adaptedPerc[L] >= 50) addToBucket(adaptedBuckets, L, adaptedPerc[L]);
            if (naturalPerc[L] >= 50) addToBucket(naturalBuckets, L, naturalPerc[L]);
          }
        });

        loadedRecords.sort((a, b) => getTimestampMillis(b) - getTimestampMillis(a));

        setRecords(loadedRecords);
      } catch (err) {
        setError(String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [db, scopeLimit]);


  const companyOptions = useMemo(() => {
    const map = new Map();

    records.forEach((record) => {
      const access = record.somerenAccess || record.access || {};
      const key = access.companyId || access.companyName || "";
      const label = access.companyName || access.companyId || "";

      if (key && label) map.set(key, label);
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [records]);

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const now = Date.now();

    return records.filter((record) => {
      const access = record.somerenAccess || record.access || {};
      const participant = record.participant || {};
      const companyKey = access.companyId || access.companyName || "";
      const generationKey = participant.generationRange || "";
      const roleKey = participant.roleLevel || "";

      if (companyFilter !== "all" && companyKey !== companyFilter) return false;
      if (generationFilter !== "all" && generationKey !== generationFilter) return false;
      if (roleFilter !== "all" && roleKey !== roleFilter) return false;

      if (dateFilter !== "all") {
        const days = Number(dateFilter);
        const cutoff = now - days * 24 * 60 * 60 * 1000;
        const recordTime = getTimestampMillis(record);
        if (!recordTime || recordTime < cutoff) return false;
      }

      if (term) {
        const haystack = [
          getParticipantName(record),
          access.companyName,
          access.companyId,
          access.teamName,
          access.teamId,
          record.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [records, searchTerm, companyFilter, generationFilter, roleFilter, dateFilter]);

  const filteredChartData = useMemo(
    () => buildChartDataFromRecords(filteredRecords, A.discDagilimi),
    // A.discDagilimi bağımlılıkta ŞART: dil değişince grafik etiketi yenilenmeli.
    [filteredRecords, A.discDagilimi]
  );

  const primaryCounts = useMemo(
    () => countBy(filteredRecords, (record) => getPrimaryStyle(record), lang),
    [filteredRecords, lang]
  );

  const generationCounts = useMemo(
    () => countBy(filteredRecords, (record) => getGenerationLabel(record?.participant?.generationRange, lang), lang),
    [filteredRecords, lang]
  );

  const roleCounts = useMemo(
    () => countBy(filteredRecords, (record) => getRoleLabel(record?.participant?.roleLevel, lang), lang),
    [filteredRecords, lang]
  );

  // Filtre değişince ilk sayfaya dön. Yoksa 3. sayfadayken filtreyi daraltan
  // kişi boş tabloya bakar ve "kayıt kayboldu" sanır.
  useEffect(() => {
    setSayfa(1);
  }, [scopeLimit, companyFilter, dateFilter, generationFilter, roleFilter, searchTerm]);

  const reportSummaryCount = filteredRecords.filter((record) => record.reportSummary).length;

  // ── Sayfalama ──
  const SAYFA_BOYU = 30;
  const toplamSayfa = Math.max(1, Math.ceil(filteredRecords.length / SAYFA_BOYU));
  // Filtre daraldığında mevcut sayfa aralık dışında kalabilir; son sayfaya çek.
  const gecerliSayfa = Math.min(sayfa, toplamSayfa);
  const ilkIndeks = (gecerliSayfa - 1) * SAYFA_BOYU;
  const latestRecords = filteredRecords.slice(ilkIndeks, ilkIndeks + SAYFA_BOYU);

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>{A.baslik}</h2>

      {error && <p style={styles.error}>{A.hata}: {error}</p>}

      {loading ? (
        <p style={{ textAlign: "center", marginTop: 40 }}>{A.yukleniyor}</p>
      ) : (
        <>
          <div style={styles.kpiGrid}>
            <Kpi title={A.okunanKayit} value={records.length} />
            <Kpi title={A.filtrelenenKayit} value={filteredRecords.length} />
            <Kpi title={A.raporOzetiOlan} value={reportSummaryCount} />
            <Kpi title={A.listelenenKisi} value={latestRecords.length} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 14px" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{A.raporDili}</span>
            <select
              value={raporDili}
              onChange={(e) => setRaporDili(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, cursor: "pointer" }}
            >
              <option value="arayuz">{A.arayuzIleAyni}</option>
              <option value="kayit">{A.kaydinDili}</option>
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>

          <FilterPanel
            scopeLimit={scopeLimit}
            setScopeLimit={setScopeLimit}
            companyFilter={companyFilter}
            setCompanyFilter={setCompanyFilter}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            generationFilter={generationFilter}
            setGenerationFilter={setGenerationFilter}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            companyOptions={companyOptions}
            onScopeChange={() => setSelectedRecord(null)}
            lang={lang}
          />

          <div style={styles.breakdownGrid}>
            <Breakdown title={A.baskinDagilim} data={primaryCounts} />
            <Breakdown title={A.jenerasyonKirilimi} data={generationCounts} />
            <Breakdown title={A.rolKirilimi} data={roleCounts} />
          </div>

          {/* İki dağılım YAN YANA: uyarlanmış ve doğal grafiği alt alta okumak,
              aynı kişinin iki noktasını kafada eşleştirmeyi zorlaştırıyordu.
              Dar ekranda tek sütuna düşer. */}
          <label style={styles.isimAnahtari}>
            <input
              type="checkbox"
              checked={isimGoster}
              onChange={(e) => setIsimGoster(e.target.checked)}
            />
            <span>
              <b>{A.isimleriGoster}</b>
              <span style={styles.isimAnahtariNot}>
                {isimGoster
                  ? A.ipucuAcik
                  : A.ipucuKapali}
              </span>
            </span>
          </label>

          <div style={styles.dagilimIzgara}>
            <div>
              <h3 style={styles.dagilimBaslik}>{A.uyarlanmisDagilim}</h3>
              <ChartBox data={filteredChartData.adaptedData} options={chartOptions} />
            </div>
            <div>
              <h3 style={styles.dagilimBaslik}>{A.dogalDagilim}</h3>
              <ChartBox data={filteredChartData.naturalData} options={chartOptions} />
            </div>
          </div>

          <div style={styles.listeBasligi}>
            <h3 style={{ margin: 0 }}>{A.kisiListesi(filteredRecords.length)}</h3>
            {toplamSayfa > 1 && (
              <div style={styles.sayfaCubugu}>
                <span style={styles.sayfaBilgi}>
                  {A.sayfaBilgi(ilkIndeks + 1, ilkIndeks + latestRecords.length, filteredRecords.length)}
                </span>
                <button
                  type="button"
                  style={{ ...styles.sayfaOk, ...(gecerliSayfa === 1 ? styles.sayfaPasif : null) }}
                  onClick={() => setSayfa((n) => Math.max(1, n - 1))}
                  disabled={gecerliSayfa === 1}
                >‹</button>
                {Array.from({ length: toplamSayfa }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setSayfa(n)}
                    aria-current={n === gecerliSayfa ? "page" : undefined}
                    style={{ ...styles.sayfaDugme, ...(n === gecerliSayfa ? styles.sayfaAktif : null) }}
                  >{n}</button>
                ))}
                <button
                  type="button"
                  style={{ ...styles.sayfaOk, ...(gecerliSayfa === toplamSayfa ? styles.sayfaPasif : null) }}
                  onClick={() => setSayfa((n) => Math.min(toplamSayfa, n + 1))}
                  disabled={gecerliSayfa === toplamSayfa}
                >›</button>
              </div>
            )}
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{A.sutunTarih}</th>
                  <th style={styles.th}>{A.sutunSure}</th>
                  <th style={styles.th}>{A.sutunKatilimci}</th>
                  <th style={styles.th}>{A.sutunSirketTakim}</th>
                  <th style={styles.th}>{A.sutunJenerasyon}</th>
                  <th style={styles.th}>{A.sutunRol}</th>
                  <th style={styles.th}>{A.sutunUyarli}</th>
                  <th style={styles.th}>{A.sutunDogal}</th>
                  {/* Boşluk = "-" grubuna düşen, hiçbir harfe puan yazmayan seçimler.
                      Şablonda "en çok" tarafında 25, "en az" tarafında 21 sözcük böyledir.
                      Kişi bazlı görünürlük istendi: yüksek boşluk, harflerin daha az
                      veriyle hesaplandığı anlamına gelir. */}
                  <th style={styles.th} title={A.boslukIpucu}>{A.sutunBosluk}<br />M · L</th>
                  <th style={styles.th}>{A.sutunIslem}</th>
                </tr>
              </thead>
              <tbody>
                {latestRecords.map((record) => {
                  const access = record.somerenAccess || {};
                  const participant = record.participant || {};
                  return (
                    <tr key={record.id}>
                      <td style={styles.td}>{formatDate(record)}</td>
                      <td style={styles.td}>{formatDuration(getDurationMs(record), "tr")}</td>
                      <td style={styles.td}>{getParticipantName(record)}</td>
                      <td style={styles.td}>
                        <strong>{access.companyName || "-"}</strong>
                        <br />
                        <small>{access.teamName || "-"}</small>
                      </td>
                      <td style={styles.td}>{getGenerationLabel(participant.generationRange, lang)}</td>
                      <td style={styles.td}>{getRoleLabel(participant.roleLevel, lang)}</td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <DiscProfileLetters scores={getAdaptedScores(record)} />
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <DiscProfileLetters scores={getNaturalScores(record)} />
                      </td>
                      <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                        {(() => {
                          const sy = sayimlariCikar(record.answers);
                          if (!sy.toplam) return <span style={{ color: "#9ca3af" }}>-</span>;
                          const vurgu = (v) => (v >= 8 ? styles.bosYuksek : styles.bosNormal);
                          return (
                            <span title={A.boslukOzeti(sy.toplam, sy.mostBos, sy.leastBos)}>
                              <b style={vurgu(sy.mostBos)}>{sy.mostBos}</b>
                              <span style={{ color: "#9ca3af" }}> · </span>
                              <b style={vurgu(sy.leastBos)}>{sy.leastBos}</b>
                            </span>
                          );
                        })()}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.islemKutu}>
                          <button style={styles.smallButton} onClick={() => setSelectedRecord(record)}>
                            {A.raporuAc}
                          </button>
                          <button
                            style={styles.pdfButton}
                            onClick={() => { setYazdirmaModu("tam"); setYazdirilacakKayit(record); }}
                            title={A.pdfIpucu}
                          >
                            PDF
                          </button>
                          <button
                            style={styles.pdfButtonKatilimci}
                            onClick={() => { setYazdirmaModu("katilimci"); setYazdirilacakKayit(record); }}
                            title={A.katilimciPdfIpucu}
                          >
                            {A.katilimciPdf}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selectedRecord && (
            <div style={styles.selectedReport}>
              <div style={styles.selectedTop}>
                <h3 style={{ margin: 0 }}>{A.secilenKisiRaporu}</h3>
                <div style={styles.islemKutu}>
                  <button style={styles.pdfButtonBuyuk} onClick={() => { setYazdirmaModu("tam"); setYazdirilacakKayit(selectedRecord); }}>
                    {A.pdfKaydet}
                  </button>
                  <button style={styles.pdfButton} onClick={() => { setYazdirmaModu("katilimci"); setYazdirilacakKayit(selectedRecord); }}>
                    {A.katilimciPdf}
                  </button>
                  <button style={styles.closeButton} onClick={() => setSelectedRecord(null)}>
                    {A.kapat}
                  </button>
                </div>
              </div>

              <DiscPersonalReport {...raporOzellikleri(selectedRecord, raporDili, lang)} />
            </div>
          )}

          {yazdirilacakKayit && (
            <RaporYazdirma
              katilimciAdi={getParticipantName(yazdirilacakKayit)}
              sirket={yazdirilacakKayit?.somerenAccess?.companyName || ""}
              takim={yazdirilacakKayit?.somerenAccess?.teamName || ""}
              tarih={formatDate(yazdirilacakKayit)}
              sureMetni={formatDuration(getDurationMs(yazdirilacakKayit), raporDiliSec(yazdirilacakKayit, raporDili, lang))}
              baskinHarf={getPrimaryStyle(yazdirilacakKayit)}
              dil={raporDiliSec(yazdirilacakKayit, raporDili, lang)}
              raporProps={raporOzellikleri(yazdirilacakKayit, raporDili, lang)}
              katilimciGorunumu={yazdirmaModu === "katilimci"}
              onKapat={() => setYazdirilacakKayit(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function FilterPanel({
  scopeLimit,
  setScopeLimit,
  companyFilter,
  setCompanyFilter,
  dateFilter,
  setDateFilter,
  generationFilter,
  setGenerationFilter,
  roleFilter,
  setRoleFilter,
  searchTerm,
  setSearchTerm,
  companyOptions,
  onScopeChange,
  lang = "tr",
}) {
  const A = getAdminText(lang);
  function handleScopeChange(value) {
    setScopeLimit(value);
    onScopeChange?.();
  }

  return (
    <div style={styles.filterPanel}>
      <h3 style={styles.filterTitle}>{A.filtreler}</h3>

      <div style={styles.filterGrid}>
        <label style={styles.filterLabel}>
          {A.kapsam}
          <select
            value={scopeLimit}
            onChange={(event) => handleScopeChange(event.target.value)}
            style={styles.filterInput}
          >
            <option value="30">{A.son30}</option>
            <option value="100">{A.son100}</option>
            <option value="250">{A.son250}</option>
            <option value="all">{A.tumKayitlar}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          {A.filtreSirket}
          <select
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {companyOptions.map((company) => (
              <option key={company.value} value={company.value}>
                {company.label}
              </option>
            ))}
          </select>
        </label>

        <label style={styles.filterLabel}>
          {A.filtreTarih}
          <select
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            <option value="7">{A.son7Gun}</option>
            <option value="30">{A.son30Gun}</option>
            <option value="90">{A.son90Gun}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          {A.filtreJenerasyon}
          <select
            value={generationFilter}
            onChange={(event) => setGenerationFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {Object.entries(GENERATION_LABELS(lang)).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="">{A.belirtilmedi}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          {A.filtreRolSeviyesi}
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            style={styles.filterInput}
          >
            <option value="all">{A.tumu}</option>
            {Object.entries(ROLE_LABELS(lang)).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="">{A.belirtilmedi}</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          {A.filtreArama}
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={A.araPlaceholder}
            style={styles.filterInput}
          />
        </label>
      </div>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div style={styles.kpi}>
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Breakdown({ title, data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div style={styles.card}>
      <h4 style={styles.cardTitle}>{title}</h4>
      {entries.length === 0 ? (
        <p style={styles.muted}>Veri yok</p>
      ) : (
        <div style={styles.breakdownList}>
          {entries.map(([key, count]) => (
            <div key={key} style={styles.breakdownRow}>
              <span>{key}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartBox({ data, options }) {
  return (
    <div style={styles.chartOuter}>
      <div style={styles.chartInner}>
        <Scatter
          data={data}
          options={options}
          plugins={[quadrantPlugin]}
          style={{ position: "absolute", inset: 0 }}
        />
      </div>
    </div>
  );
}

const styles = {
  listeBasligi: { display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, flexWrap: "wrap", margin: "26px 0 10px" },
  sayfaCubugu: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" },
  sayfaBilgi: { fontSize: 12, color: "#6b7280", marginRight: 6, fontVariantNumeric: "tabular-nums" },
  sayfaDugme: { minWidth: 30, height: 30, borderRadius: 8, border: "1px solid #d1d5db",
    background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer",
    fontVariantNumeric: "tabular-nums" },
  sayfaAktif: { background: "#111827", borderColor: "#111827", color: "#fff" },
  sayfaOk: { width: 30, height: 30, borderRadius: 8, border: "1px solid #d1d5db",
    background: "#fff", color: "#374151", fontSize: 15, lineHeight: 1, cursor: "pointer" },
  sayfaPasif: { opacity: 0.35, cursor: "default" },
  page: {
    padding: 18,
    maxWidth: 1200,
    margin: "0 auto",
    color: "#111827",
  },
  title: {
    textAlign: "center",
    marginBottom: 20,
  },
  error: {
    color: "crimson",
    textAlign: "center",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  filterPanel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  filterTitle: {
    margin: "0 0 12px",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 12,
  },
  filterLabel: {
    display: "grid",
    gap: 6,
    fontSize: 13,
    fontWeight: 800,
    color: "#111827",
  },
  filterInput: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "9px 10px",
    background: "#ffffff",
    color: "#111827",
  },
  kpi: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    display: "grid",
    gap: 6,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  breakdownGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    marginBottom: 24,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },
  cardTitle: {
    margin: "0 0 10px",
  },
  muted: {
    color: "#6b7280",
    margin: 0,
  },
  breakdownList: {
    display: "grid",
    gap: 8,
  },
  breakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: 6,
    fontSize: 14,
  },
  chartOuter: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto 32px auto",
  },
  chartInner: {
    width: "100%",
    aspectRatio: "1 / 1",
    position: "relative",
  },
  tableWrap: {
    overflowX: "auto",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    marginBottom: 24,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
  },
  styleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    height: 28,
    borderRadius: 999,
    color: "#ffffff",
    fontWeight: 900,
  },
  smallButton: {
    border: "0",
    borderRadius: 10,
    background: "#111827",
    color: "#ffffff",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  islemKutu: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  bosNormal: { color: "#374151" },
  bosYuksek: { color: "#9a3412", background: "#fff7ed", padding: "1px 5px", borderRadius: 6 },
  dagilimIzgara: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
    gap: 18,
    alignItems: "start",
    marginTop: 8,
  },
  dagilimBaslik: { margin: "0 0 8px", fontSize: 16 },
  isimAnahtari: {
    display: "flex", alignItems: "flex-start", gap: 8, margin: "16px 0 4px",
    padding: "9px 12px", borderRadius: 10, border: "1px solid #e3e7ec",
    background: "#fff", fontSize: 13, cursor: "pointer", maxWidth: 640,
  },
  isimAnahtariNot: { color: "#6b7280", fontWeight: 400 },
  pdfButton: {
    border: "1px solid #f59e0b",
    borderRadius: 10,
    background: "#fffbeb",
    color: "#92400e",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  pdfButtonKatilimci: {
    border: "1px solid #5c8ed8",
    borderRadius: 10,
    background: "#eff5fd",
    color: "#1e3a8a",
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  pdfButtonBuyuk: {
    border: "0",
    borderRadius: 10,
    background: "#f59e0b",
    color: "#111827",
    padding: "9px 14px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  selectedReport: {
    borderTop: "2px solid #e5e7eb",
    marginTop: 28,
    paddingTop: 18,
  },
  selectedTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  closeButton: {
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#ffffff",
    color: "#111827",
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 800,
  },
};
